/**
 * Mode Step Component
 *
 * Step 0: Choose calculation mode - Full Room, Ceiling Only, or Individual Walls.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEstimator } from '../../../context/EstimatorContext';
import { useAppSettings } from '../../../context/AppSettingsContext';
import { CalculationMode } from '../../../types';
import { trackAdvancedDesignerOpened, trackProjectTypeSelected } from '../../../utils/gtmTracking';
import { useAdvancedDesignerEnabled } from '../../designer/advanced/useAdvancedDesignerEnabled';
import {
  Box,
  Check,
  DraftingCompass,
  Lightbulb,
  Video,
} from 'lucide-react';
import './Steps.css';

// Custom icon for Ceiling Only - top face of a cube, vertically centered
const CeilingOnlyIcon: React.FC<{ size?: number; strokeWidth?: number }> = ({
  size = 48,
  strokeWidth = 1.5
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Top face of cube — centered vertically in viewBox */}
    <path d="M21 12 L12 7 L3 12 L12 17 Z" />
  </svg>
);

// Custom icon for Individual Walls - exploded cube with equal gaps
const ExplodedBoxIcon: React.FC<{ size?: number; strokeWidth?: number }> = ({
  size = 48,
  strokeWidth = 1.5
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Top face — visual gap to walls matches gap between walls */}
    <path d="M21 8 L12 3 L3 8 L12 13 Z" />

    {/* Left wall */}
    <path d="M2 10 L11 15 L11 23 L2 18 Z" />

    {/* Right wall */}
    <path d="M22 10 L13 15 L13 23 L22 18 Z" />
  </svg>
);

export const ModeStep: React.FC = () => {
  const { state, setCalculationMode, nextStep } = useEstimator();
  const { settings } = useAppSettings();
  const { t } = useTranslation('wizard');
  const { calculationMode } = state;
  const tutorialEnabled = Boolean(settings.tutorialVideoUrl.trim());
  // Advanced mode (the floor planner + 3D designer) is a fourth, full-width
  // card — but not a calculation mode: it leaves the wizard for /design and
  // touches none of its state, so Exit over there lands back on this step.
  const advancedEnabled = useAdvancedDesignerEnabled();
  const navigate = useNavigate();
  const openAdvanced = () => {
    trackAdvancedDesignerOpened();
    navigate('/design');
  };

  const handleModeChange = (mode: CalculationMode) => {
    setCalculationMode(mode);
    trackProjectTypeSelected(mode);
  };

  const handleDoubleClick = (mode: CalculationMode) => {
    setCalculationMode(mode);
    trackProjectTypeSelected(mode);
    nextStep();
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">{t('mode.title')}</h2>
        <p className="step-description">
          {t('mode.description')}
        </p>
        {tutorialEnabled && (
          <div className="text-center">
            <button
              type="button"
              className="btn btn-link p-0 d-inline-flex align-items-center gap-1 mode-tutorial-link"
              onClick={() => window.dispatchEvent(new Event('show-tutorial-video'))}
            >
              <Video size={12} aria-hidden="true" />
              <span>{settings.tutorialMainLinkText || t('common:tutorial.mainLink')}</span>
            </button>
          </div>
        )}
      </div>

      <div className="form-section">
        <div className="row g-3 g-lg-4" role="radiogroup" aria-label={t('mode.radioGroupLabel')}>
          {/* Full Room Mode */}
          <div className="col-12 col-md-4">
            <button
              type="button"
              role="radio"
              aria-checked={calculationMode === 'FullRoom'}
              className={`mode-card ${calculationMode === 'FullRoom' ? 'selected' : ''}`}
              onClick={() => handleModeChange('FullRoom')}
              onDoubleClick={() => handleDoubleClick('FullRoom')}
            >
              <div className="mode-icon">
                <Box size={96} strokeWidth={0.8} />
              </div>
              <span className="mode-title">{t('mode.fullRoom.title')}</span>
              <p className="mode-description">
                {t('mode.fullRoom.description')}
              </p>
              <ul className="mode-features">
                <li><Check size={12} /> {t('mode.fullRoom.features.sameConfig')}</li>
                <li><Check size={12} /> {t('mode.fullRoom.features.optionalCeiling')}</li>
                <li><Check size={12} /> {t('mode.fullRoom.features.slatWall')}</li>
                <li><Check size={12} /> {t('mode.fullRoom.features.garageDoors')}</li>
              </ul>
            </button>
          </div>

          {/* Ceiling Only Mode */}
          <div className="col-12 col-md-4">
            <button
              type="button"
              role="radio"
              aria-checked={calculationMode === 'CeilingOnly'}
              className={`mode-card ${calculationMode === 'CeilingOnly' ? 'selected' : ''}`}
              onClick={() => handleModeChange('CeilingOnly')}
              onDoubleClick={() => handleDoubleClick('CeilingOnly')}
            >
              <div className="mode-icon">
                <CeilingOnlyIcon size={96} strokeWidth={0.8} />
              </div>
              <span className="mode-title">{t('mode.ceilingOnly.title')}</span>
              <p className="mode-description">
                {t('mode.ceilingOnly.description')}
              </p>
              <ul className="mode-features">
                <li><Check size={12} /> {t('mode.ceilingOnly.features.singleArea')}</li>
                <li><Check size={12} /> {t('mode.ceilingOnly.features.multiZone')}</li>
                <li><Check size={12} /> {t('mode.ceilingOnly.features.quick')}</li>
              </ul>
            </button>
          </div>

          {/* Individual Walls Mode */}
          <div className="col-12 col-md-4">
            <button
              type="button"
              role="radio"
              aria-checked={calculationMode === 'IndividualWalls'}
              className={`mode-card ${calculationMode === 'IndividualWalls' ? 'selected' : ''}`}
              onClick={() => handleModeChange('IndividualWalls')}
              onDoubleClick={() => handleDoubleClick('IndividualWalls')}
            >
              <div className="mode-icon">
                <ExplodedBoxIcon size={96} strokeWidth={0.8} />
              </div>
              <span className="mode-title">{t('mode.individualWalls.title')}</span>
              <p className="mode-description">
                {t('mode.individualWalls.description')}
              </p>
              <ul className="mode-features">
                <li><Check size={12} /> {t('mode.individualWalls.features.unlimited')}</li>
                <li><Check size={12} /> {t('mode.individualWalls.features.colors')}</li>
                <li><Check size={12} /> {t('mode.individualWalls.features.orientations')}</li>
                <li><Check size={12} /> {t('mode.individualWalls.features.optionalCeiling')}</li>
              </ul>
            </button>
          </div>
        </div>

        {advancedEnabled && (
          <div className="row g-3 g-lg-4 mt-0">
            <div className="col-12">
              <button
                type="button"
                className="mode-card mode-card--wide"
                onClick={openAdvanced}
              >
                <div className="mode-icon">
                  <DraftingCompass size={64} strokeWidth={0.9} />
                </div>
                <div className="mode-card-body">
                  <span className="mode-title">{t('mode.advanced.title')}</span>
                  <p className="mode-description">
                    {t('mode.advanced.description')}
                  </p>
                  <ul className="mode-features">
                    <li><Check size={12} /> {t('mode.advanced.features.blueprint')}</li>
                    <li><Check size={12} /> {t('mode.advanced.features.threeD')}</li>
                    <li><Check size={12} /> {t('mode.advanced.features.staging')}</li>
                  </ul>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mode-specific tips */}
      <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
        <div className="flex-shrink-0 text-info">
          <Lightbulb size={24} />
        </div>
        {/* One tip per mode, keyed by the CalculationMode value itself — the
            three branches were identical apart from their copy. */}
        <div>
          <strong>{t(`mode.tips.${calculationMode}.title`)}</strong>
          <p className="mb-0 small mt-1">
            {t(`mode.tips.${calculationMode}.body`)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModeStep;
